import React, { useState, useEffect, useMemo } from "react";
import { Group, Rect } from "react-konva";
import { DEPARTMENT_ID, NAME_ID, PHOTO_ID, ROLE_ID } from "shared/datamodel/schemas/org-chart";
import { getStripeBoundaries, OrgChartNodeTextStyles, calculateFieldLayout } from "./utils";
import { OrgchartOpenSidePanelButton } from "../shared/orgchart-open-sidepanel-button";
import { OrgchartPersonPhoto } from "../shared/orgchart-person-photo";
import { OrgchartSimpleInputField } from "./orgchart-simple-input-field";
import { KonvaEventObject } from "konva/types/Node";
import { TEXT_NODE_COLOR } from "shared/datamodel/mindmap-org-chart";
import { lightenColorMore } from "utils/color-utils";
import { OrgChartViewOnlyTextField } from "./orgchart-view-only-text-field";
import OrgChartNodeBackground from "./orgchart-node-background";
import { TextFieldHeight } from "../shared/consts";
import { NodeDrawProps } from "../shared/types";
import { Point } from "shared/datamodel/schemas";
import { Sizes } from "frontend/data/shapes/sizes";

interface ReadOnlyNodeProps {
  id: string;
  type: string;
  isRoot: boolean;
  position: Point;
  width: number;
  height: number;
  fieldsToShow: string[];
  fieldsTitle: Record<string, { name: string }>;
  showImage: boolean;
  data: any;
  nodeDrawProps: NodeDrawProps;
  element: any;

  isSidePanelOpenForMe: boolean;
  onOpenPanel: any;
  onClick?: (ev: KonvaEventObject<MouseEvent>) => void;
  isExporting: boolean;
  isTextOnly?: boolean;
  text?: string;
  children?: React.ReactNode | undefined;
  //allow extra props
  [key: string]: any;
}

interface NodeProps extends ReadOnlyNodeProps {
  isSelected: boolean;
  isHovered?: boolean;
  editingFieldId: string;
  setEditingFieldId: any;
  onFieldUpdate: any;
  onDragStart?: (ev: any) => void;
  onPhotoClick?: () => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  //allow extra props
  [key: string]: any;
}

export function OrgChartNode({
  id,
  type,
  isRoot,
  position,
  width,
  height,
  fieldsToShow,
  fieldsTitle,
  showImage,
  data,
  nodeDrawProps,
  element,
  isSelected,
  editingFieldId,
  setEditingFieldId,
  onFieldUpdate,
  isSidePanelOpenForMe,
  onOpenPanel,
  onClick,
  onDragStart,
  onPhotoClick,
  onMouseEnter,
  onMouseLeave,
  isHovered,
  isExporting,
  children,
  ...rest
}: NodeProps) {
  const { color, lineWidth, dash } = nodeDrawProps;
  const strokeColor = color;
  const strokeWidth = lineWidth;
  const top = position.y - height / 2,
    left = position.x - width / 2;
  const bgColor = lightenColorMore(strokeColor);
  const sizes = Sizes.get().orgChart;
  const HOVER_AREA_PADDING = (sizes.thumbnailRadius * 2 + 12) * 2;

  // State for real-time text values during editing
  const [realtimeData, setRealtimeData] = useState<Record<string, string>>({});

  // Create merged data for layout calculation (real-time values override original data)
  // Ensure real-time data is processed the same way as saved data (trimmed)
  const mergedData = useMemo(() => {
    const processedRealtimeData: Record<string, string> = {};
    for (const [key, value] of Object.entries(realtimeData)) {
      processedRealtimeData[key] = value.trim();
    }
    return { ...data, ...processedRealtimeData };
  }, [data, realtimeData]);

  // Calculate field layout for dynamic heights and positions using real-time data with caching
  const fieldLayout = useMemo(() => {
    return calculateFieldLayout(mergedData, fieldsToShow, showImage, sizes);
  }, [mergedData, fieldsToShow, showImage, sizes]);

  // Calculate dynamic stripe boundaries with 6px padding around custom fields
  const stripeBoundaries = getStripeBoundaries(fieldLayout, fieldsToShow);

  // Handle real-time text changes
  const handleRealTimeChange = (fieldId: string, currentValue: string) => {
    setRealtimeData((previous) => ({
      ...previous,
      [fieldId]: currentValue,
    }));
  };

  // Handle the transition when editing finishes - set final value before clearing
  const handleFieldFinish = (fieldId: string, final: string, initial: string) => {
    // Update realtime data to the final trimmed value first to prevent height jumping
    setRealtimeData((previous) => ({
      ...previous,
      [fieldId]: final,
    }));

    // Call the actual field update
    onFieldUpdate(fieldId, final, initial);
  };

  // Clear real-time data when editing ends, but with a delay to prevent jumping
  useEffect(() => {
    if (!editingFieldId) {
      // Small delay to allow parent to update element.data first
      const timeoutId = setTimeout(() => {
        setRealtimeData({});
      }, 50);

      return () => clearTimeout(timeoutId);
    }
  }, [editingFieldId]);

  // Use dynamic height consistently - this ensures smooth transitions
  const effectiveHeight = fieldLayout.totalContentHeight;

  const bgProps = {
    stripeColor: fieldsToShow.length > 0 ? bgColor : undefined,
    stripeStart: stripeBoundaries.stripeStart,
    stripeEnd: stripeBoundaries.stripeEnd,
    borderRadius: sizes.borderRadius,
  };

  function renderField(fieldId: string, fieldTitle: string, initialValue: string, textStyleId: OrgChartNodeTextStyles) {
    const fieldHeight = fieldLayout.heights[fieldId] || TextFieldHeight;
    const fieldY = fieldLayout.positions[fieldId] || 0;
    const textPadding = 4;

    return (
      <OrgchartSimpleInputField
        key={fieldId}
        id={fieldId}
        isEditing={isSelected && editingFieldId == fieldId}
        listening={isSelected && !element.lock}
        setIsEditing={(state) => setEditingFieldId(state ? fieldId : "")}
        rect={{ x: textPadding, width: width - textPadding * 2, height: fieldHeight, y: fieldY }}
        initialValue={initialValue}
        onFinish={(initial, final) => handleFieldFinish(fieldId, final, initial)}
        onRealTimeChange={handleRealTimeChange}
        fieldTitle={fieldTitle}
        textStyleId={textStyleId}
        textColor={strokeColor}
      />
    );
  }

  // Some notes:
  // x,y are undefined for internal nodes, since we they should be draggable
  // by Konva events; so the top group's position is not controlled.
  // except for the root node, which is controlled by the canvas-stage.

  return (
    <Group
      type={type}
      x={isRoot ? element.x : undefined}
      y={isRoot ? element.y : undefined}
      id={id}
      name={id}
      element={element}
      isCanvasElement={true}
      isSelectable={true}
      isConnectable={false}
      isConnector={false}
      isFrame={false}
      isDraggable={isRoot} // this means: is draggable by canvas-stage
      draggable={!isRoot && !element.lock && !!onDragStart} // this means: is draggable by Konva events
      onDragStart={onDragStart}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      {...rest}
    >
      <Group x={left} y={top}>
        {isHovered && !isSelected && (
          <Rect
            width={width}
            height={effectiveHeight}
            fill="transparent"
            hitStrokeWidth={HOVER_AREA_PADDING}
            onMouseDown={(e) => (e.cancelBubble = true)}
          />
        )}
        <OrgChartNodeBackground
          width={width}
          height={effectiveHeight}
          strokeColor={strokeColor}
          strokeWidth={strokeWidth}
          dash={dash}
          bgProps={bgProps}
        />
        {/* Header */}
        {showImage && <OrgchartPersonPhoto x={width / 2} y={0} photoUrl={data?.[PHOTO_ID]} onClick={onPhotoClick} />}
        {renderField(NAME_ID, "Employee Name", mergedData[NAME_ID], "h1")}
        {renderField(ROLE_ID, "Role", mergedData[ROLE_ID], "h2")}
        {/* Body */}
        {fieldsToShow.map((fieldId: string) =>
          renderField(fieldId, fieldsTitle[fieldId].name || " -- ", mergedData[fieldId], "default")
        )}
        {!isExporting && (
          <OrgchartOpenSidePanelButton
            cx={width - 18 * sizes.widgetScale}
            cy={8 * sizes.widgetScale}
            color={strokeColor}
            hoverColor={lightenColorMore(color)}
            highlight={isSidePanelOpenForMe}
            onClick={onOpenPanel}
          />
        )}
      </Group>
      <Group x={position.x} y={position.y}>
        {children}
      </Group>
    </Group>
  );
}

export function OrgChartViewOnlyNode({
  id,
  type,
  isRoot,
  position,
  width,
  height,
  fieldsToShow,
  fieldsTitle,
  showImage,
  data,
  nodeDrawProps,
  isSidePanelOpenForMe,
  onOpenPanel,
  onClick,
  isExporting,
  isTextOnly,
  text,
  children,
  element,
  ...rest
}: ReadOnlyNodeProps) {
  const top = position.y - height / 2;
  const left = position.x - width / 2;
  const sizes = Sizes.get().orgChart;
  const fieldLayout = calculateFieldLayout(data, fieldsToShow, showImage, sizes);

  const stripeBoundaries = getStripeBoundaries(fieldLayout, fieldsToShow);

  function getNodeBgProps() {
    const { color, lineWidth, dash } = nodeDrawProps;
    const strokeColor = color;
    const strokeWidth = lineWidth;

    let bgColor =
      fieldsToShow.length > 0
        ? lightenColorMore(strokeColor) === "#ffffff"
          ? lightenColorMore(strokeColor, 0.3)
          : lightenColorMore(strokeColor)
        : undefined;
    let borderRadius = 0;

    // Use the new stripe boundaries for custom fields
    let stripeStart = stripeBoundaries.stripeStart;
    let stripeEnd = stripeBoundaries.stripeEnd;

    if (isTextOnly) {
      bgColor = TEXT_NODE_COLOR;
      stripeStart = strokeWidth / 2;
      stripeEnd = fieldLayout.totalContentHeight;
      borderRadius = 2;
    }

    const bgProps = {
      stripeColor: bgColor,
      stripeStart,
      stripeEnd,
      borderRadius,
    };

    return { color, dash, strokeColor, strokeWidth, bgProps };
  }

  const { color, dash, strokeColor, strokeWidth, bgProps } = getNodeBgProps();

  // Use dynamic height from field layout for view-only nodes too
  const effectiveHeight = fieldLayout.totalContentHeight;

  function renderField(fieldId: string, fieldTitle: string, initialValue: string, textStyleId: OrgChartNodeTextStyles) {
    const fieldHeight = fieldLayout.heights[fieldId] || TextFieldHeight;
    const fieldY = fieldLayout.positions[fieldId] || 0;
    const textPadding = 4;

    return (
      <OrgChartViewOnlyTextField
        key={fieldId}
        rect={{ x: textPadding, width: width - textPadding * 2, height: fieldHeight, y: fieldY }}
        initialValue={initialValue}
        fieldTitle={fieldTitle}
        textStyleId={textStyleId}
        textColor={strokeColor}
      />
    );
  }

  return (
    <Group
      type={type}
      x={isRoot ? element.x : undefined}
      y={isRoot ? element.y : undefined}
      id={id}
      name={id}
      element={element}
      isCanvasElement={true}
      isSelectable={true}
      isConnectable={false}
      isConnector={false}
      isFrame={false}
      isDraggable={isRoot} // this means: is draggable by canvas-stage
      draggable={false} // this means: is draggable by Konva events
      onClick={onClick}
      {...rest}
    >
      <Group x={left} y={top}>
        <OrgChartNodeBackground
          width={width}
          height={effectiveHeight}
          strokeColor={strokeColor}
          strokeWidth={strokeWidth}
          dash={dash}
          bgProps={bgProps}
        />

        {!isTextOnly || !text ? (
          <>
            {/* Header */}
            {showImage && <OrgchartPersonPhoto x={width / 2} y={0} photoUrl={data?.[PHOTO_ID]} />}
            {renderField(NAME_ID, "Employee Name", data[NAME_ID], "h1")}
            {renderField(ROLE_ID, "Role", data[ROLE_ID], "h2")}
            {/* Body */}
            {fieldsToShow.map((fieldId: string) =>
              renderField(fieldId, fieldsTitle[fieldId].name || "--", data[fieldId], "default")
            )}
            {!isExporting && (
              <OrgchartOpenSidePanelButton
                cx={width - 19}
                cy={8}
                color={strokeColor}
                hoverColor={lightenColorMore(color)}
                highlight={isSidePanelOpenForMe}
                onClick={onOpenPanel}
              />
            )}
          </>
        ) : (
          <OrgChartViewOnlyTextField
            key={DEPARTMENT_ID}
            rect={{ x: 4, width: width - 8, height, y: 0 }}
            initialValue={text}
            fieldTitle="Department"
            textStyleId="sourceText"
            textColor={strokeColor}
          />
        )}
      </Group>

      <Group x={position.x} y={position.y}>
        {children}
      </Group>
    </Group>
  );
}
